'use client';

import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Callout, Tabs } from '@galapagos/design-system/web';
import { FormActions } from '../components/FormActions';
import { PageTitle } from '../components/PageTitle';
import { SignupShell } from '../components/SignupShell';
import {
  EMAIL_METHOD,
  PHONE_METHOD,
  SignInContactFields,
  signInMethodFor,
  useSignInContactForm,
} from '../components/SignInContactFields';
import { ensureSignupIdentityStyle } from '../components/signup-identity-style';
import { ensureSignupStepStyle } from '../components/signup-step-style';
import { useSignupApi } from '../hooks/useSignupApi';
import { useLegacySignupParams } from '../hooks/useLegacySignupParams';
import { useSignupNavigation } from '../hooks/useSignupNavigation';
import { useStepHeading } from '../hooks/useStepHeading';
import { SIGNUP_LOGIN_CODE_PAGE } from '../off-path-pages';
import { sendFailureStatus } from '../services/send-failure-status';
import { useSignupFlowStore, type SignupSignInContact } from '../stores/signup-flow-store';

const FIELD_TEST_IDS = { phone: 'signup-login-phone', email: 'signup-login-email' } as const;

function LoginStep() {
  // Every flow page refills the sponsor params from its own URL.
  useLegacySignupParams();
  const { t } = useTranslation('signup');
  const title = t('login.title');
  const headingRef = useStepHeading(title);
  const api = useSignupApi();
  const { goToStep } = useSignupNavigation();
  const setSignInContact = useSignupFlowStore((state) => state.setSignInContact);
  const setOtpResendAfterSeconds = useSignupFlowStore((state) => state.setOtpResendAfterSeconds);
  // Back from the code step after a not-found answer, the tab used stays selected.
  const contactForm = useSignInContactForm(
    signInMethodFor(useSignupFlowStore.getState().signInContact),
  );
  const setLoginNotice = useSignupFlowStore((state) => state.setLoginNotice);
  const [status, setStatus] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // A ref, not the state above, guards re-entry: two presses in one tick both
  // see the state before it updates.
  const submittingRef = useRef(false);
  // A send that completes after the visitor left this page must not store a
  // contact or move the flow on.
  const liveRef = useRef(true);
  useEffect(() => {
    liveRef.current = true;
    return () => {
      liveRef.current = false;
    };
  }, []);
  // A code step can leave a notice. It is shown once, after mount, so the
  // always-mounted status region announces the change.
  useEffect(() => {
    const notice = useSignupFlowStore.getState().loginNotice;
    if (notice === null) return;
    setLoginNotice(null);
    setStatus(
      t(
        notice === 'contact-on-another-account'
          ? 'login.contactOnAnotherAccount'
          : 'login.accountNotFound',
      ),
    );
  }, [setLoginNotice, t]);
  ensureSignupStepStyle();
  ensureSignupIdentityStyle();

  const sendCode = async (contact: SignupSignInContact) => {
    const flowAtSend = useSignupFlowStore.getState().params;
    const sent = await api.initiateOtp(contact);
    if (!liveRef.current || useSignupFlowStore.getState().params !== flowAtSend) return;
    setSignInContact(contact);
    setOtpResendAfterSeconds(sent.resend_available_in_seconds ?? null);
    goToStep(SIGNUP_LOGIN_CODE_PAGE);
  };

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (submittingRef.current) return;
    const read = contactForm.readContact();
    contactForm.setErrors(read.errors);
    if (read.contact === null) return;
    const contact = read.contact;
    submittingRef.current = true;
    setSubmitting(true);
    setStatus('');
    try {
      await sendCode(contact);
    } catch (error) {
      if (!liveRef.current) return;
      if (!contactForm.showFieldRejection(error)) setStatus(sendFailureStatus(t, error));
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  return (
    <SignupShell>
      <form className="signup-step" noValidate onSubmit={onSubmit}>
        <PageTitle title={title} description={t('login.description')} headingRef={headingRef} />
        <Tabs
          fullWidth
          items={[
            { key: PHONE_METHOD, label: t('account.tabs.phone') },
            { key: EMAIL_METHOD, label: t('account.tabs.email') },
          ]}
          activeKey={contactForm.method}
          onChange={(key) => {
            contactForm.setMethod(key === EMAIL_METHOD ? EMAIL_METHOD : PHONE_METHOD);
            setStatus('');
          }}
          accessibilityLabel={t('login.methodLabel')}
          actionId="signup.login-screen-method.change"
          data-testid="signup-login-method"
        />
        <SignInContactFields form={contactForm} testIds={FIELD_TEST_IDS} />
        {/* The one status announcer for this page: always mounted, its text
            changes. The visible callout is hidden from assistive technology so
            the message is not announced twice. */}
        <p className="signup-visually-hidden" aria-live="polite" data-testid="signup-status">
          {status}
        </p>
        {status ? (
          <div aria-hidden="true">
            <Callout tone="error" body={status} />
          </div>
        ) : null}
        <FormActions>
          <Button
            size="L"
            type="Primary"
            label={t('login.send')}
            htmlType="submit"
            state={submitting ? 'Loading' : 'Default'}
            actionId="signup.login-screen-continue.activate"
            data-testid="signup-login-send"
          />
        </FormActions>
      </form>
    </SignupShell>
  );
}

/**
 * `/login/`: a returning member asks for a one-time code by cell phone
 * (the default) or email (production R01 and R01b, on v9 1.4's shell).
 */
export function LoginStepScreen() {
  return <LoginStep />;
}
